"use client";

import { useSyncExternalStore } from "react";

const QUERY = "(prefers-reduced-motion: reduce)";

// useSyncExternalStore is the React-19-blessed pattern for subscribing
// to non-React stores (matchMedia, history, localStorage, etc.). It
// reads the current value synchronously on every render — no useEffect
// needed, no SSR/CSR mismatch when the store is queried via the third
// `getServerSnapshot` arg.
//
// Earlier implementation used useState + useEffect with
// `setReduced(mql.matches)` on mount; eslint-plugin-react-hooks v6
// flags that as `set-state-in-effect` (rightly so — the value should
// be read during render, not asynchronously after).
//
// `subscribe` is module-level so identity is stable across renders.

function subscribe(callback: () => void): () => void {
  const mql = window.matchMedia(QUERY);
  mql.addEventListener("change", callback);
  return () => mql.removeEventListener("change", callback);
}

function getSnapshot(): boolean {
  return window.matchMedia(QUERY).matches;
}

// SSR returns false — assume motion is allowed until the client
// proves otherwise. Same default the old hook used pre-mount.
function getServerSnapshot(): boolean {
  return false;
}

/**
 * Subscribes to `prefers-reduced-motion: reduce`. Returns `true` when the OS
 * setting is on. Re-renders the consumer when the setting flips, so inline
 * `style={}` transitions can be removed reactively.
 *
 * SSR-safe: server returns `false`, client reads the actual value on first
 * render. For fire-and-forget callbacks (scrollIntoView), read
 * `window.matchMedia` inline instead of using this hook — no subscription
 * needed.
 */
export function useReducedMotion(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
